import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { authAPI, demoAPI } from '../lib/api';
import { setToken, isAuthenticated } from '../lib/auth';
import { markOnboardingComplete, clearOnboardingFlag } from '../lib/onboarding';
import { useAuth } from '../App';
import { useTranslation } from '../lib/i18n';
import { track } from '../lib/analytics';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import RCLogo from '../components/Logo';
import { Sparkles, Mail, Loader2, KeyRound, ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react';

export default function AuthPage() {
  const { t } = useTranslation();
  // Three modes: login / signup / forgot. The legacy `isLogin`
  // derived value below keeps existing conditional logic working
  // without a sweeping rewrite. Forgot mode just renders an
  // entirely different form (email-only) inside the same Card shell.
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'forgot' ? 'forgot' : 'login';
  const [mode, setMode] = useState(initialMode);
  const isLogin = mode === 'login';
  const isForgot = mode === 'forgot';

  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Set on a sign-up attempt that fails the client checks: highlights
  // what's missing. The submit button stays tappable so a tap never
  // silently does nothing (users on phones missed the small rule list).
  const [showIssues, setShowIssues] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
  });

  // Forgot-password sub-state. `forgotEmail` is independent of the
  // login `formData.email` so toggling between modes doesn't clobber
  // the user's typing. `forgotSent` flips to true on a successful
  // POST AND on any error. The backend's anti-enumeration contract
  // is that the response shape is identical either way, so we don't
  // distinguish at the UI layer either.
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Show/hide-password toggle for the login + signup password field.
  // Lets the user verify their typing on a phone keyboard where
  // mis-taps are common. Defaults to hidden. Toggle is opt-in per
  // session and resets on every page load.
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { authenticated, markAuthenticated } = useAuth();

  // `?redirect=/some/path` lets callers bounce a logged-out user
  // through here and resume their original intent on the other side.
  // We sanitise to same-origin paths only. Never honour an absolute
  // URL or `//host/...` form (open-redirect classic).
  const safeRedirect = (() => {
    const raw = searchParams.get('redirect');
    if (!raw) return '/dashboard';
    if (!raw.startsWith('/') || raw.startsWith('//')) return '/dashboard';
    return raw;
  })();

  // `?demo=<token>`. Viral-funnel claim. When set, after successful
  // auth we call /demo/<token>/claim to attach the anonymous render
  // to the new account, then navigate the user to that property's
  // page so they immediately see their first video. Token is opaque
  // (URL-safe base64) so no further sanitisation is needed.
  const demoToken = searchParams.get('demo');

  // Pulls the demo render into the user's account. Called after a
  // fresh signup/login when a demo token is present in the URL.
  // Returns the route to navigate to (property detail, with a
  // celebratory toast). Or null if there was no token / claim
  // failed silently. We never block the auth success path on this:
  // worst-case the user lands on /dashboard and the demo is lost,
  // but they're still signed up.
  const claimDemoIfPresent = async () => {
    if (!demoToken) return null;
    try {
      const { data } = await demoAPI.claim(demoToken);
      if (data?.property_id) {
        toast.success(
          t('demo.claimSuccess')
            || 'Dein Demo-Video wurde deinem Konto hinzugefügt.'
        );
        return `/properties/${data.property_id}`;
      }
    } catch (err) {
      // Don't toast the error. It'd interrupt the welcome flow.
      // Logging is enough; the user still has the freshly-created
      // account and can manually find/render again.
      // eslint-disable-next-line no-console
      console.warn('Demo claim failed:', err?.response?.data || err);
    }
    return null;
  };

  useEffect(() => {
    if (location.state?.error) {
      toast.error(location.state.error);
    }
    // Redirect only when the AuthProvider *context* says we're logged in –
    // not when localStorage alone has a token. Otherwise ProtectedRoute would
    // bounce us right back here because its source of truth is the context.
    if (authenticated) {
      // Already-signed-in user landing here with a demo token (clicked
      // the share-page CTA while logged in). Claim then route to the
      // property. Async, but the navigate runs synchronously after
      // resolution.
      if (demoToken) {
        (async () => {
          const target = await claimDemoIfPresent();
          navigate(target || safeRedirect);
        })();
      } else {
        navigate(safeRedirect);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, navigate, authenticated, safeRedirect, demoToken]);

  /**
   * Forgot-password submit. We always show the same "if we have your
   * email…" confirmation, regardless of whether the backend found a
   * user. This mirrors the backend's anti-enumeration response and
   * prevents an attacker from probing which addresses are registered
   * by watching for an error toast.
   */
  const handleForgotSubmit = async (e) => {
    e?.preventDefault();
    const email = forgotEmail.trim().toLowerCase();
    if (!email || forgotLoading) return;
    setForgotLoading(true);
    try {
      await authAPI.forgotPassword(email);
    } catch (err) {
      // Swallow per anti-enumeration. Rate-limit (429) is the only
      // case where surfacing the error is useful. Tell the user to
      // wait so they don't keep retrying and triggering it again.
      if (err?.response?.status === 429) {
        toast.error(
          t('auth.forgotRateLimit')
            || 'Zu viele Anfragen. Bitte warte eine Stunde und versuche es erneut.'
        );
        setForgotLoading(false);
        return;
      }
      // Network errors / 5xx. Same generic confirmation so we don't
      // leak server status to a probing attacker.
    }
    setForgotSent(true);
    setForgotLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Sign-up checks, mirrored server-side. Say exactly what's missing.
    if (!isLogin) {
      const missing = [
        formData.password.length < 10 && t('auth.passwordRuleLength'),
        !/[A-Za-z]/.test(formData.password) && t('auth.passwordRuleLetter'),
        !/\d/.test(formData.password) && t('auth.passwordRuleDigit'),
      ].filter(Boolean);
      if (missing.length) {
        setShowIssues(true);
        toast.error(t('auth.passwordMissing', { rules: missing.join(', ') }));
        setLoading(false);
        return;
      }
    }

    // A7. Gate register on terms acceptance. Defense in depth: backend
    // also rejects, but failing fast in the UI gives a clear toast.
    if (!isLogin && !termsAccepted) {
      setShowIssues(true);
      toast.error(t('auth.acceptTermsRequired') || 'Bitte akzeptiere AGB und Datenschutzerklärung.');
      setLoading(false);
      return;
    }

    try {
      const response = isLogin
        ? await authAPI.login({ email: formData.email, password: formData.password })
        : await authAPI.register({ ...formData, terms_accepted: true });

      setToken(response.data.access_token);
      // A13. Funnel event. Fires before getMe so the analytics call
      // doesn't get cancelled if the page navigates fast.
      track(isLogin ? 'login_completed' : 'signup_completed');
      // Sync onboarding status from backend (server is source of truth).
      try {
        const me = await authAPI.getMe();
        if (me.data.onboarding_completed) {
          markOnboardingComplete();
        } else {
          clearOnboardingFlag();
        }
      } catch (_) {}
      // Push the new auth state into the context BEFORE navigating so
      // ProtectedRoute doesn't bounce us back to /auth on the next render.
      markAuthenticated(true);
      toast.success(
        isLogin
          ? t('auth.loginSuccess') || 'Welcome back!'
          : t('auth.registerSuccess') || 'Account created successfully!'
      );
      // Viral funnel: if the user came here via /auth?demo=<token>,
      // claim the anonymous render and navigate to that property's
      // detail page. Failures fall through silently to safeRedirect.
      const demoTarget = await claimDemoIfPresent();
      navigate(demoTarget || safeRedirect);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.authFailed') || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f7f3ea] relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 bg-gradient-to-tr from-[#1f4d3a]/10 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#dfe8db] rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#f0e6cf] rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md mx-4 z-10">
        <div className="text-center mb-8">
          <div
            className="flex items-center justify-center mb-3 cursor-pointer"
            onClick={() => navigate('/')}
            role="button"
            aria-label={t('auth.logoToHome') || 'Real Content. Zur Startseite'}
          >
            <RCLogo size="lg" />
          </div>
          <p className="text-[#6f6a5c] mt-2">{t('auth.tagline') || 'Automate your real estate social media'}</p>
        </div>

        <Card className="glass border-[#e3dccb] shadow-2xl">
          {isForgot ? (
            // ── Forgot-password mode ──────────────────────────────
            // Two sub-views: the email-entry form, and the
            // post-submit confirmation. Always show the confirmation
            // regardless of whether the backend found the user
            // (mirrors the anti-enumeration contract on the server).
            <>
              <CardHeader>
                <CardTitle className="text-2xl font-semibold text-center inline-flex items-center justify-center gap-2">
                  <KeyRound className="w-6 h-6 text-[#1f4d3a]" />
                  {t('auth.forgotTitle') || 'Passwort vergessen'}
                </CardTitle>
                <CardDescription className="text-center">
                  {forgotSent
                    ? (t('auth.forgotSentSubtitle') || 'Schau in dein E-Mail-Postfach.')
                    : (t('auth.forgotSubtitle') || 'Wir schicken dir einen Link zum Zurücksetzen.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {forgotSent ? (
                  <div className="text-center py-4 space-y-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#e4f0e8] mx-auto">
                      <CheckCircle2 className="w-8 h-8 text-[#1e7a4f]" />
                    </div>
                    <p className="text-sm text-[#4a463b] leading-relaxed">
                      {t('auth.forgotSentBody')
                        || 'Falls ein Konto mit dieser E-Mail existiert, haben wir dir einen Link zum Zurücksetzen geschickt. Der Link ist 30 Minuten gültig.'}
                    </p>
                    <p className="text-xs text-[#8a8474]">
                      {t('auth.forgotSentHint')
                        || 'Keine E-Mail erhalten? Schau im Spam-Ordner nach oder versuche es in ein paar Minuten erneut.'}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setMode('login');
                        setForgotSent(false);
                      }}
                      className="text-sm text-[#1f4d3a] hover:text-[#1f4d3a] inline-flex items-center gap-1 transition-colors"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      {t('auth.backToLogin') || 'Zurück zur Anmeldung'}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                        {t('auth.email') || 'Email'}
                      </label>
                      <Input
                        data-testid="forgot-email-input"
                        type="email"
                        placeholder="you@example.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        autoFocus
                        autoComplete="email"
                        className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] focus:ring-[#1f4d3a]/20"
                      />
                    </div>
                    <Button
                      data-testid="forgot-submit-button"
                      type="submit"
                      className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary transition-all duration-300"
                      disabled={forgotLoading || !forgotEmail.trim()}
                    >
                      {forgotLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('common.loading') || 'Wird gesendet…'}
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          {t('auth.forgotSubmit') || 'Reset-Link anfordern'}
                        </>
                      )}
                    </Button>
                    <div className="text-center pt-1">
                      <button
                        type="button"
                        onClick={() => setMode('login')}
                        className="text-sm text-[#6f6a5c] hover:text-[#1f4d3a] inline-flex items-center gap-1 transition-colors"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        {t('auth.backToLogin') || 'Zurück zur Anmeldung'}
                      </button>
                    </div>
                  </form>
                )}
              </CardContent>
            </>
          ) : (
          <>
          <CardHeader>
            <CardTitle className="text-2xl font-semibold text-center">
              {isLogin
                ? (t('auth.welcomeBack') || 'Welcome Back')
                : (t('auth.signUp') || 'Get Started')}
            </CardTitle>
            <CardDescription className="text-center">
              {isLogin
                ? (t('auth.signInSubtitle') || 'Sign in to your account')
                : (t('auth.signUpSubtitle') || 'Create your account')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                    {t('auth.fullName') || 'Full Name'}
                  </label>
                  <Input
                    data-testid="auth-fullname-input"
                    type="text"
                    placeholder={t('auth.fullNamePlaceholder') || 'Max Mustermann'}
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required={!isLogin}
                    className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] focus:ring-[#1f4d3a]/20"
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                  {t('auth.email') || 'Email'}
                </label>
                <Input
                  data-testid="auth-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] focus:ring-[#1f4d3a]/20"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                  {t('auth.password') || 'Password'}
                </label>
                {/* Input + right-edge eye-toggle. Wrapper is `relative`
                    so the button can absolute-position over the input
                    without affecting layout. Input gets `pr-10` padding
                    so the typed text doesn't slide under the icon. */}
                <div className="relative">
                  <Input
                    data-testid="auth-password-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={isLogin ? undefined : 10}
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] focus:ring-[#1f4d3a]/20 pr-10"
                  />
                  <button
                    type="button"
                    data-testid="auth-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword
                        ? (t('auth.hidePassword') || 'Passwort verbergen')
                        : (t('auth.showPassword') || 'Passwort anzeigen')
                    }
                    aria-pressed={showPassword}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#8a8474] hover:text-[#4a463b] transition-colors rounded focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {/* Live password-policy feedback on the Sign-Up variant.
                    Each rule turns from slate → emerald as it's met, so
                    the user gets immediate "what's still missing" without
                    having to hit Submit and read a server error. We
                    deliberately don't show this on Login (no policy on
                    the way in. Only on the way out at register time). */}
                {!isLogin && (formData.password.length > 0 || showIssues) && (
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                    <span className={
                      formData.password.length >= 10
                        ? 'text-[#1e7a4f]'
                        : showIssues ? 'text-[#a34430] font-medium' : 'text-[#8a8474]'
                    }>
                      {formData.password.length >= 10 ? '✓' : '○'}{' '}
                      {t('auth.passwordRuleLength') || 'Mind. 10 Zeichen'}
                    </span>
                    <span className={
                      /[A-Za-z]/.test(formData.password)
                        ? 'text-[#1e7a4f]'
                        : showIssues ? 'text-[#a34430] font-medium' : 'text-[#8a8474]'
                    }>
                      {/[A-Za-z]/.test(formData.password) ? '✓' : '○'}{' '}
                      {t('auth.passwordRuleLetter') || 'Ein Buchstabe'}
                    </span>
                    <span className={
                      /\d/.test(formData.password)
                        ? 'text-[#1e7a4f]'
                        : showIssues ? 'text-[#a34430] font-medium' : 'text-[#8a8474]'
                    }>
                      {/\d/.test(formData.password) ? '✓' : '○'}{' '}
                      {t('auth.passwordRuleDigit') || 'Eine Ziffer'}
                    </span>
                  </div>
                )}
                {/* "Passwort vergessen?". Only on the Login variant.
                    Lives directly under the password input so the user
                    sees it at the moment they realise they can't recall
                    the password, not buried below the submit button. */}
                {isLogin && (
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      data-testid="auth-forgot-link"
                      onClick={() => setMode('forgot')}
                      className="text-xs text-[#6f6a5c] hover:text-[#1f4d3a] transition-colors"
                    >
                      {t('auth.forgotLink') || 'Passwort vergessen?'}
                    </button>
                  </div>
                )}
              </div>

              {/* A7. DSGVO consent. Required to enable submit on the
                  Sign-Up variant; hidden on the Login variant. */}
              {!isLogin && (
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                    showIssues && !termsAccepted
                      ? 'border-[#b3543f] bg-[#f7e8e3]'
                      : 'border-[#e3dccb] bg-[#f2ecdf] hover:border-[#c9c1ac]'}`}
                  data-testid="auth-terms-checkbox-wrap"
                >
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 accent-[#1f4d3a] cursor-pointer shrink-0"
                    data-testid="auth-terms-checkbox"
                  />
                  <span className="text-xs text-[#4a463b] leading-relaxed">
                    {t('auth.termsConsentPrefix') || 'Ich habe die'}{' '}
                    <Link to="/agb" target="_blank" className="text-[#1f4d3a] hover:text-[#1f4d3a] underline">
                      {t('auth.termsLink') || 'AGB'}
                    </Link>
                    {' '}{t('auth.termsAnd') || 'und die'}{' '}
                    <Link to="/datenschutz" target="_blank" className="text-[#1f4d3a] hover:text-[#1f4d3a] underline">
                      {t('auth.privacyLink') || 'Datenschutzerklärung'}
                    </Link>
                    {' '}{t('auth.termsConsentSuffix') || 'gelesen und akzeptiere sie.'}
                  </span>
                </label>
              )}

              <Button
                data-testid="auth-submit-button"
                type="submit"
                className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary transition-all duration-300"
                // Only disabled while a request is in flight. Sign-up rules
                // (password policy, terms) are checked on submit with a
                // message saying what's missing, see handleSubmit.
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.loading') || 'Processing…'}
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    {isLogin
                      ? (t('auth.signIn') || 'Sign In')
                      : (t('auth.signUp') || 'Create Account')}
                  </>
                )}
              </Button>
            </form>

            <div className="text-center">
              <button
                data-testid="auth-toggle-button"
                // mode='login' ↔ 'signup'. setIsLogin no longer exists
                // (converted to derived value when the forgot-password
                // flow added a 3rd mode); going through setMode keeps
                // the three states (login / signup / forgot) coherent.
                onClick={() => setMode(isLogin ? 'signup' : 'login')}
                className="text-sm text-[#6f6a5c] hover:text-[#1f4d3a] transition-colors"
              >
                {isLogin
                  ? (t('auth.noAccount') || "Don't have an account?")
                  : (t('auth.hasAccount') || 'Already have an account?')}{' '}
                <span className="underline">
                  {isLogin
                    ? (t('auth.signUp') || 'Sign up')
                    : (t('auth.signIn') || 'Sign in')}
                </span>
              </button>
            </div>

            {/* Risk reversal pills. Only shown on the Sign-Up variant.
                Matching the landing page's hero badges so the confidence
                signal carries through from marketing to sign-up. */}
            {/* Risk-reversal pills. Only on Sign-Up. The previous copy
                claimed "Keine Kreditkarte" which conflicts with A4's
                anti-abuse SetupIntent (we DO collect a card, but
                without charging). New copy is honest about that. */}
            {!isLogin && (
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-[#8a8474] pt-3 border-t border-[#e3dccb]">
                <span>✓ 5 Videos gratis</span>
                <span>✓ Karte zur Verifizierung (€0 Belastung)</span>
                <span>✓ Jederzeit kündbar</span>
              </div>
            )}
          </CardContent>
          </>
          )}
        </Card>
      </div>
    </div>
  );
}
